/**
 * gtmDataLayer.js — GA4 Enhanced Ecommerce dataLayer wrapper.
 *
 * Pattern (per Google's 2026 GA4 spec):
 *   1. Reset the prior `ecommerce` block first via
 *      `window.dataLayer.push({ ecommerce: null })` so values from a
 *      previous event don't leak into the next one.
 *   2. Push the named event with a fully-populated `ecommerce` object
 *      whose `items` array uses the GA4 recommended parameter set
 *      (`item_id`, `item_name`, `item_brand`, `item_category`,
 *      `item_variant`, `price`, `quantity`, `index`).
 *
 * This module is intentionally side-effect free — no React imports, no
 * network calls. Every function is safe to invoke from SSR, web workers,
 * or non-browser contexts because they short-circuit on missing
 * `window.dataLayer` (which GTM's base snippet defines).
 *
 * Pairs with metaPixel.js — both fire from the same hook points but
 * targeted at different vendors. Order is: Meta first (smaller payload,
 * fewer reasons to fail), GA4/GTM second. They never share state.
 */

const BRAND = "Sri Durga Foods";
const AFFILIATION = "sridurgafoods.com";
const CURRENCY = "INR";

function dl() {
  if (typeof window === "undefined") return null;
  if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
  return window.dataLayer;
}

function push(payload) {
  const queue = dl();
  if (!queue) return;
  try {
    queue.push(payload);
  } catch (_) {
    /* analytics must NEVER throw into the UI */
  }
}

/**
 * Reset the ecommerce block before every ecommerce-shaped event.
 * GA4 explicitly requires this; otherwise stale `items` from a prior
 * event get aggregated by GTM's "Ecommerce" variable.
 */
function resetEcommerce() {
  push({ ecommerce: null });
}

/**
 * Convert a product (and optional selected variant) into a GA4 item object.
 *
 * @param {object} product   — { product_id|id, name, category, brand?, ... }
 * @param {object} [variant] — { label, price, ... }
 * @param {number} [quantity]
 * @param {number} [index]
 */
function toItem(product, variant, quantity = 1, index) {
  if (!product) return null;
  const price = Number(variant?.price ?? product.price ?? 0) || 0;
  const item = {
    item_id: String(product.product_id || product.id || product.name || ""),
    item_name: product.name || "",
    item_brand: product.brand || BRAND,
    item_category: product.category || "",
    price,
    quantity: Number(quantity) || 1,
    currency: CURRENCY,
  };
  if (variant?.label || variant?.variant_label) {
    item.item_variant = variant.label || variant.variant_label;
  }
  if (typeof index === "number") item.index = index;
  return item;
}

function cartItemsToGa4(items) {
  return (items || []).map((it, idx) =>
    toItem(
      { product_id: it.product_id || it.id, name: it.name, category: it.category, brand: it.brand },
      { label: it.variant_label, price: it.price },
      it.quantity,
      idx,
    ),
  ).filter(Boolean);
}

function totalValue(items) {
  return (items || []).reduce((sum, i) => sum + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0);
}

// ── Engagement ──────────────────────────────────────────────────────────
export function pushPageView(path) {
  push({
    event: "page_view",
    page_path: path || (typeof window !== "undefined" ? window.location.pathname + window.location.search : ""),
    page_location: typeof window !== "undefined" ? window.location.href : "",
    page_title: typeof document !== "undefined" ? document.title : "",
  });
}

export function pushLogin(method = "email") {
  push({ event: "login", method });
}

export function pushSignUp(method = "email") {
  push({ event: "sign_up", method });
}

export function pushSearch(searchTerm, resultsCount) {
  if (!searchTerm) return;
  push({
    event: "search",
    search_term: String(searchTerm).slice(0, 200),
    ...(typeof resultsCount === "number" ? { search_results: resultsCount } : {}),
  });
}

/**
 * Generic lead-style engagement event — used for WhatsApp clicks, click-to-
 * call, email-click, and contact-form submissions. GA4's `generate_lead`
 * event with a `method` parameter is the recommended way to consolidate
 * these in one funnel.
 */
export function pushGenerateLead(method, extra = {}) {
  push({
    event: "generate_lead",
    method,
    currency: CURRENCY,
    ...extra,
  });
}

export function pushOutboundClick(url, linkText) {
  push({
    event: "click",                  // GA4 enhanced-measurement style
    link_classes: "outbound",
    link_url: url,
    link_text: (linkText || "").slice(0, 200),
    outbound: true,
  });
}

// ── Ecommerce ───────────────────────────────────────────────────────────
export function pushViewItemList(products, { listId, listName } = {}) {
  if (!Array.isArray(products) || products.length === 0) return;
  resetEcommerce();
  push({
    event: "view_item_list",
    ecommerce: {
      item_list_id: listId || "default",
      item_list_name: listName || "Products",
      items: products.map((p, idx) =>
        toItem(p, null, 1, idx),
      ).filter(Boolean),
    },
  });
}

export function pushSelectItem(product, { listId, listName, index } = {}) {
  if (!product) return;
  resetEcommerce();
  push({
    event: "select_item",
    ecommerce: {
      item_list_id: listId || "default",
      item_list_name: listName || "Products",
      items: [toItem(product, null, 1, index)].filter(Boolean),
    },
  });
}

export function pushViewItem(product, variant) {
  if (!product) return;
  const item = toItem(product, variant, 1);
  resetEcommerce();
  push({
    event: "view_item",
    ecommerce: {
      currency: CURRENCY,
      value: item?.price || 0,
      items: [item].filter(Boolean),
    },
  });
}

export function pushAddToCart(product, variant, quantity = 1) {
  if (!product) return;
  const item = toItem(product, variant, quantity);
  if (!item) return;
  resetEcommerce();
  push({
    event: "add_to_cart",
    ecommerce: {
      currency: CURRENCY,
      value: item.price * item.quantity,
      items: [item],
    },
  });
}

export function pushRemoveFromCart(product, variant, quantity = 1) {
  if (!product) return;
  const item = toItem(product, variant, quantity);
  if (!item) return;
  resetEcommerce();
  push({
    event: "remove_from_cart",
    ecommerce: {
      currency: CURRENCY,
      value: item.price * item.quantity,
      items: [item],
    },
  });
}

export function pushViewCart(cartItems) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return;
  const items = cartItemsToGa4(cartItems);
  resetEcommerce();
  push({
    event: "view_cart",
    ecommerce: {
      currency: CURRENCY,
      value: totalValue(cartItems),
      items,
    },
  });
}

export function pushBeginCheckout(cartItems, { coupon, value } = {}) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return;
  const items = cartItemsToGa4(cartItems);
  resetEcommerce();
  push({
    event: "begin_checkout",
    ecommerce: {
      currency: CURRENCY,
      value: Number(value ?? totalValue(cartItems)),
      ...(coupon ? { coupon } : {}),
      items,
    },
  });
}

export function pushAddShippingInfo(cartItems, { shippingTier, value, coupon } = {}) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return;
  const items = cartItemsToGa4(cartItems);
  resetEcommerce();
  push({
    event: "add_shipping_info",
    ecommerce: {
      currency: CURRENCY,
      value: Number(value ?? totalValue(cartItems)),
      shipping_tier: shippingTier || "Standard",
      ...(coupon ? { coupon } : {}),
      items,
    },
  });
}

export function pushAddPaymentInfo(cartItems, { paymentType, value, coupon } = {}) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return;
  const items = cartItemsToGa4(cartItems);
  resetEcommerce();
  push({
    event: "add_payment_info",
    ecommerce: {
      currency: CURRENCY,
      value: Number(value ?? totalValue(cartItems)),
      payment_type: paymentType || "razorpay",
      ...(coupon ? { coupon } : {}),
      items,
    },
  });
}

/**
 * Pushes `purchase`. Idempotent — call once per order confirmation page
 * load (the consumer is expected to guard with sessionStorage).
 */
export function pushPurchase(order) {
  if (!order) return;
  const items = cartItemsToGa4(order.items || []);
  resetEcommerce();
  push({
    event: "purchase",
    ecommerce: {
      transaction_id: order.order_id || "",
      affiliation: AFFILIATION,
      currency: CURRENCY,
      value: Number(order.total) || 0,
      tax: Number(order.tax) || 0,
      shipping: Number(order.delivery_charge ?? order.delivery) || 0,
      ...(order.discount ? { discount: Number(order.discount) } : {}),
      ...(order.coupon ? { coupon: order.coupon } : {}),
      payment_type: order.payment_method || "razorpay",
      items,
    },
  });
}

export function pushRefund(order, { partialItems } = {}) {
  if (!order) return;
  const items = cartItemsToGa4(partialItems || order.items || []);
  resetEcommerce();
  push({
    event: "refund",
    ecommerce: {
      transaction_id: order.order_id || "",
      affiliation: AFFILIATION,
      currency: CURRENCY,
      value: Number(order.refund_amount ?? order.total) || 0,
      items,
    },
  });
}

// ── Promotions (hero carousel slots etc.) ──────────────────────────────
export function pushViewPromotion({ id, name, creative, slot, index } = {}) {
  resetEcommerce();
  push({
    event: "view_promotion",
    ecommerce: {
      items: [{
        promotion_id: id || "",
        promotion_name: name || "",
        creative_name: creative || "",
        creative_slot: slot || "",
        ...(typeof index === "number" ? { index } : {}),
      }],
    },
  });
}

export function pushSelectPromotion({ id, name, creative, slot, index } = {}) {
  resetEcommerce();
  push({
    event: "select_promotion",
    ecommerce: {
      items: [{
        promotion_id: id || "",
        promotion_name: name || "",
        creative_name: creative || "",
        creative_slot: slot || "",
        ...(typeof index === "number" ? { index } : {}),
      }],
    },
  });
}
